import { useEffect, useMemo, useRef, useState, type CSSProperties, type HTMLInputTypeAttribute, type ReactNode } from 'react';
import {
  Building2,
  CalendarDays,
  Image,
  Mail,
  RotateCcw,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import { AdminModulePage, PageHero } from '../components/PageTemplates';
import { Switch } from '../components/Switch';
import { useStore } from '../hooks/useStore';
import { US_STATES } from '../lib/usStates';
import {
  resetBranding,
  getLeague,
  updateBranding,
  updateCommunicationSettings,
  updateEventDefaults,
  updateLeague,
} from '../services/leagueService';
import { DEFAULT_LEAGUE } from '../models/league';
import type { LeagueHomeRink, PlayoffFormat, RegistrationIntakeType, ScoringSettings, SeasonRosterIntake, Weekday } from '../models';

type SettingsTab = 'profile' | 'brand' | 'defaults' | 'communications' | 'login';

interface DeliveryConfig {
  ok?: boolean;
  fromEmail?: string;
  replyToEmail?: string;
}

interface AuditRow {
  id: string;
  action?: string;
  actor_email?: string;
  record_table?: string;
  record_id?: string;
  created_at?: string;
}

const TABS: Array<{ id: SettingsTab; label: string; icon: typeof Building2 }> = [
  { id: 'profile', label: 'League Profile', icon: Building2 },
  { id: 'brand', label: 'Brand & Public Pages', icon: Image },
  { id: 'defaults', label: 'Event Defaults', icon: CalendarDays },
  { id: 'communications', label: 'Communications', icon: Mail },
  { id: 'login', label: 'Login History', icon: ShieldCheck },
];

const WEEKDAYS: Array<{ value: Weekday; label: string }> = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

const TIEBREAKER_OPTIONS: ScoringSettings['tiebreakers'][number][] = [
  'wins',
  'goal_diff',
  'goals_for',
  'head_to_head',
  'shootout_wins',
  'fewest_pims',
];

const TIEBREAKER_LABEL: Record<ScoringSettings['tiebreakers'][number], string> = {
  wins: 'Wins',
  goal_diff: 'Goal differential',
  goals_for: 'Goals for',
  head_to_head: 'Head-to-head',
  shootout_wins: 'Shootout wins',
  fewest_pims: 'Fewest penalty minutes',
};

export function SettingsPage() {
  const db = useStore((d) => d);
  const league = db.league;
  const homeRink = league.homeRink ?? { name: '', address: '', postalCode: '', city: '', region: '' };
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [divisionDraft, setDivisionDraft] = useState(league.eventDefaults.divisionLabels.join(', '));
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [deliveryConfig, setDeliveryConfig] = useState<DeliveryConfig | null>(null);
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);

  useEffect(() => {
    setDivisionDraft(league.eventDefaults.divisionLabels.join(', '));
  }, [league.eventDefaults.divisionLabels]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/communications/config', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setDeliveryConfig(data as DeliveryConfig);
      })
      .catch(() => undefined);
    fetch('/api/audit', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.audit) setAuditRows(data.audit as AuditRow[]);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const tournamentTypes = useMemo(
    () => db.tournamentTypes.filter((type) => !type.typeId.includes('season')),
    [db.tournamentTypes],
  );
  const seasonTypes = useMemo(
    () => db.tournamentTypes.filter((type) => type.typeId.includes('season') && type.typeId !== 'ironman-season'),
    [db.tournamentTypes],
  );

  const stamp = () => setSavedAt(new Date().toLocaleTimeString());
  const onLeague = (patch: Parameters<typeof updateLeague>[0]) => {
    updateLeague(patch);
    stamp();
  };
  const onHomeRink = (patch: Partial<LeagueHomeRink>) => {
    onLeague({ homeRink: { ...(getLeague().homeRink ?? homeRink), ...patch } });
  };
  const onHomeRinkZip = async (postalCode: string) => {
    const zip = postalCode.replace(/\D+/g, '').slice(0, 5);
    onHomeRink({ postalCode: zip });
    if (zip.length !== 5) return;
    const match = await lookupZip(zip);
    if (match) onHomeRink({ postalCode: zip, city: match.city, region: match.stateCode });
  };
  const setSeasonDefaultTiebreakers = (tiebreakers: ScoringSettings['tiebreakers']) => {
    updateEventDefaults({ season: { tiebreakers } });
    stamp();
  };
  const moveSeasonDefaultTiebreaker = (key: ScoringSettings['tiebreakers'][number], dir: -1 | 1) => {
    const list = [...league.eventDefaults.season.tiebreakers];
    const i = list.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setSeasonDefaultTiebreakers(list);
  };
  const toggleSeasonDefaultTiebreaker = (key: ScoringSettings['tiebreakers'][number], on: boolean) => {
    const list = on
      ? [...league.eventDefaults.season.tiebreakers, key]
      : league.eventDefaults.season.tiebreakers.filter((value) => value !== key);
    setSeasonDefaultTiebreakers(list.length ? list : ['wins']);
  };
  const onLogo = async (file: File | undefined) => {
    if (!file) return;
    setLogoBusy(true);
    setLogoError('');
    try {
      const dataBase64 = await fileToBase64(file);
      const response = await fetch('/api/assets', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          ownerType: 'league-brand',
          ownerId: 'league',
          dataBase64,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as { asset?: { id: string; fileName?: string }; error?: string };
      if (!response.ok || !data.asset) throw new Error(data.error ?? `Upload failed with ${response.status}`);
      updateBranding({ logoAssetId: data.asset.id, logoFileName: data.asset.fileName ?? file.name });
      stamp();
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Logo upload failed.');
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <AdminModulePage>
      <PageHero
        title="Settings"
        description="Configure league-wide defaults, public presentation, communications, and login history."
        icon={ShieldCheck}
        accent="arc"
        status={savedAt ? { label: `Saved ${savedAt}`, tone: 'ok' } : undefined}
      />

      <div className="tabs-bar" role="tablist" aria-label="League HQ Settings sections">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              className={`tabs-bar__tab ${activeTab === tab.id ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'profile' && (
        <SettingsGrid>
          <SettingsCard
            title="League Profile"
            description="Controls how this league appears in League HQ, event pages, registration, and message footers."
          >
            <div className="stack">
              <Field label="League name">
                <DebouncedInput value={league.name} onCommit={(value) => onLeague({ name: value })} />
              </Field>
              <Field label="Public tagline">
                <DebouncedInput value={league.tagline ?? ''} onCommit={(value) => onLeague({ tagline: value })} />
              </Field>
              <Field label="Sport" hint="Locked to roller hockey positioning for v1. This label appears in setup defaults and league-owned pages.">
                <DebouncedInput value={league.sport} onCommit={(value) => onLeague({ sport: value })} />
              </Field>
              <Field label="Public contact email" hint="Shown on league-owned public pages and used as the communication reply-to fallback.">
                <DebouncedInput type="email" value={league.contactEmail ?? ''} onCommit={(value) => onLeague({ contactEmail: value })} />
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard
            title="Home Rink"
            description="Prefills new seasons and tournaments so setup only asks for changes when needed."
          >
            <div className="stack">
              <Field label="Rink name">
                <DebouncedInput value={homeRink.name} onCommit={(value) => onHomeRink({ name: value })} />
              </Field>
              <Field label="Address">
                <DebouncedInput value={homeRink.address} onCommit={(value) => onHomeRink({ address: value })} />
              </Field>
              <div className="grid grid--3">
                <Field label="ZIP">
                  <DebouncedInput value={homeRink.postalCode ?? ''} onCommit={onHomeRinkZip} placeholder="12345" />
                </Field>
                <Field label="City">
                  <DebouncedInput value={homeRink.city ?? ''} onCommit={(value) => onHomeRink({ city: value })} />
                </Field>
                <Field label="State">
                  <select className="select" value={homeRink.region ?? ''} onChange={(event) => onHomeRink({ region: event.target.value })}>
                    <option value="">Select a state</option>
                    {US_STATES.map((state) => <option key={state.code} value={state.code}>{state.name}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          </SettingsCard>
        </SettingsGrid>
      )}

      {activeTab === 'brand' && (
        <SettingsGrid>
          <SettingsCard
            title="Brand & Public Pages"
            description="League branding for League HQ and league-owned public pages."
            wide
          >
            <div className="stack">
              <Field label="League logo" hint="Stored privately in R2 and served only as a league-brand public asset.">
                <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  {league.branding.logoAssetId ? (
                    <img
                      src={`/api/public-asset?id=${encodeURIComponent(league.branding.logoAssetId)}`}
                      alt=""
                      style={{ width: 64, height: 64, objectFit: 'contain', border: '1px solid var(--surface-line)', borderRadius: 8 }}
                    />
                  ) : (
                    <div className="empty__icon"><Image size={18} /></div>
                  )}
                  <label className="btn btn--ghost btn--sm">
                    <Upload size={14} /> {logoBusy ? 'Uploading...' : 'Upload logo'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      hidden
                      disabled={logoBusy}
                      onChange={(event) => void onLogo(event.target.files?.[0])}
                    />
                  </label>
                  {league.branding.logoAssetId && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        updateBranding({ logoAssetId: undefined, logoFileName: undefined });
                        stamp();
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                {league.branding.logoFileName && <p className="field__hint">{league.branding.logoFileName}</p>}
                {logoError && <p className="field__hint" style={{ color: 'var(--danger)' }}>{logoError}</p>}
              </Field>
              <Field label="Primary color">
                <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={league.branding.primaryColor}
                    onChange={(event) => {
                      updateBranding({ primaryColor: event.target.value });
                      stamp();
                    }}
                    style={{ width: 56, height: 40, border: 'none', background: 'transparent', cursor: 'pointer' }}
                    aria-label="Primary color picker"
                  />
                  <input
                    className="input"
                    value={league.branding.primaryColor}
                    onChange={(event) => {
                      updateBranding({ primaryColor: event.target.value });
                      stamp();
                    }}
                    style={{ maxWidth: 140, fontFamily: 'var(--font-mono, monospace)' }}
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      resetBranding({ ...DEFAULT_LEAGUE.branding });
                      stamp();
                    }}
                  >
                    <RotateCcw size={14} /> Reset
                  </button>
                </div>
              </Field>
              <Switch
                checked={league.branding.applyToPublic}
                onChange={(value) => {
                  updateBranding({ applyToPublic: value });
                  stamp();
                }}
                label="Apply brand color to public event and registration pages"
                hint="League HQ always uses the league color. The Scorekeeper company marketing site does not."
              />
            </div>
          </SettingsCard>
        </SettingsGrid>
      )}

      {activeTab === 'defaults' && (
        <SettingsGrid>
          <SettingsCard
            title="Shared Setup Defaults"
            description="Used anywhere the season or tournament setup flow needs common league defaults."
          >
            <div className="stack">
              <Field label="Default division labels" hint="Comma-separated. These appear as quick-add chips in the roster manager.">
                <input
                  className="input"
                  value={divisionDraft}
                  onChange={(event) => setDivisionDraft(event.target.value)}
                  onBlur={() => {
                    const labels = splitList(divisionDraft);
                    updateEventDefaults({ divisionLabels: labels.length ? labels : ['Adult'] });
                    setDivisionDraft((labels.length ? labels : ['Adult']).join(', '));
                    stamp();
                  }}
                />
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard
            title="Tournament Defaults"
            description="Prefills the guided tournament setup flow."
          >
            <div className="stack">
              <div className="grid grid--2">
                <Field label="Default tournament preset">
                  <select
                    className="select"
                    value={league.eventDefaults.tournamentTypeId}
                    onChange={(event) => {
                      updateEventDefaults({ tournamentTypeId: event.target.value });
                      stamp();
                    }}
                  >
                    {tournamentTypes.map((type) => <option key={type.typeId} value={type.typeId}>{type.label}</option>)}
                  </select>
                </Field>
                <Field label="Default rink count">
                  <input
                    type="number"
                    min={1}
                    max={12}
                    className="input"
                    value={league.eventDefaults.rinkCount}
                    onChange={(event) => {
                      updateEventDefaults({ rinkCount: clampInt(event.target.value, 1, 12) });
                      stamp();
                    }}
                  />
                </Field>
              </div>
              <Field label="Rink name pattern" hint="Use {{n}} for the rink number. Example: Court {{n}}.">
                <DebouncedInput value={league.eventDefaults.rinkNamePattern} onCommit={(value) => {
                  updateEventDefaults({ rinkNamePattern: value });
                  stamp();
                }} />
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard title="Season Schedule & Rules" description="Prefills the cadence and rules steps in the guided season setup flow.">
            <div className="stack">
              <Field label="Default season preset">
                <select
                  className="select"
                  value={league.eventDefaults.seasonTypeId}
                  onChange={(event) => {
                    updateEventDefaults({ seasonTypeId: event.target.value });
                    stamp();
                  }}
                >
                  {seasonTypes.map((type) => <option key={type.typeId} value={type.typeId}>{type.label}</option>)}
                </select>
              </Field>
              <Field label="Game nights">
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {WEEKDAYS.map((day) => {
                    const checked = league.eventDefaults.season.weekdays.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        className={`btn btn--sm ${checked ? 'btn--primary' : 'btn--ghost'}`}
                        onClick={() => {
                          const weekdays = checked
                            ? league.eventDefaults.season.weekdays.filter((value) => value !== day.value)
                            : Array.from(new Set([...league.eventDefaults.season.weekdays, day.value]));
                          updateEventDefaults({ season: { weekdays } });
                          stamp();
                        }}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <div className="grid grid--2">
                <Field label="First puck time">
                  <input
                    type="time"
                    className="input"
                    value={league.eventDefaults.season.firstPuckTime}
                    onChange={(event) => {
                      updateEventDefaults({ season: { firstPuckTime: event.target.value } });
                      stamp();
                    }}
                  />
                </Field>
                <Field label="Games per rink/night">
                  <input
                    type="number"
                    min={1}
                    max={12}
                    className="input"
                    value={league.eventDefaults.season.gamesPerRinkPerNight}
                    onChange={(event) => {
                      updateEventDefaults({ season: { gamesPerRinkPerNight: clampInt(event.target.value, 1, 12) } });
                      stamp();
                    }}
                  />
                </Field>
              </div>
              <div className="grid grid--2">
                <Field label="Periods">
                  <input
                    type="number"
                    min={1}
                    max={6}
                    className="input"
                    value={league.eventDefaults.season.periodCount}
                    onChange={(event) => {
                      updateEventDefaults({ season: { periodCount: clampInt(event.target.value, 1, 6) } });
                      stamp();
                    }}
                  />
                </Field>
                <Field label="Minutes per period">
                  <input
                    type="number"
                    min={1}
                    max={30}
                    className="input"
                    value={league.eventDefaults.season.periodLengthMinutes}
                    onChange={(event) => {
                      updateEventDefaults({ season: { periodLengthMinutes: clampInt(event.target.value, 1, 30) } });
                      stamp();
                    }}
                  />
                </Field>
              </div>
              <div className="note">
                Game length: {league.eventDefaults.season.periodCount * league.eventDefaults.season.periodLengthMinutes} regulation minutes.
              </div>
              <div className="grid grid--2">
                <Field label="Tie game rule">
                  <select
                    className="select"
                    value={league.eventDefaults.season.overtimeFormat ?? 'straight_shootout'}
                    onChange={(event) => {
                      updateEventDefaults({ season: { overtimeFormat: event.target.value as typeof league.eventDefaults.season.overtimeFormat } });
                      stamp();
                    }}
                  >
                    <option value="straight_shootout">Straight to shootout</option>
                    <option value="overtime_then_shootout">Overtime, then shootout</option>
                    <option value="continuous_overtime">Continuous sudden-death overtime</option>
                    <option value="regulation_tie">Allow ties after regulation</option>
                  </select>
                </Field>
                <Field label="Shootout round size">
                  <input
                    type="number"
                    min={1}
                    max={10}
                    className="input"
                    value={league.eventDefaults.season.shootoutRoundCount ?? 3}
                    onChange={(event) => {
                      updateEventDefaults({ season: { shootoutRoundCount: clampInt(event.target.value, 1, 10) } });
                      stamp();
                    }}
                  />
                </Field>
              </div>
              <div className="grid grid--3">
                <Field label="OT minutes">
                  <input
                    type="number"
                    min={1}
                    max={20}
                    className="input"
                    value={league.eventDefaults.season.overtimeMinutes ?? 5}
                    onChange={(event) => {
                      updateEventDefaults({ season: { overtimeMinutes: clampInt(event.target.value, 1, 20) } });
                      stamp();
                    }}
                  />
                </Field>
                <Field label="OT skaters">
                  <input
                    type="number"
                    min={1}
                    max={5}
                    className="input"
                    value={league.eventDefaults.season.overtimeSkaters ?? 3}
                    onChange={(event) => {
                      updateEventDefaults({ season: { overtimeSkaters: clampInt(event.target.value, 1, 5) } });
                      stamp();
                    }}
                  />
                </Field>
                <Field label="OT/SO loss points">
                  <input
                    type="number"
                    min={0}
                    max={3}
                    className="input"
                    value={league.eventDefaults.season.pointsOvertimeLoss ?? 1}
                    onChange={(event) => {
                      const value = clampInt(event.target.value, 0, 3);
                      updateEventDefaults({ season: { pointsOvertimeLoss: value, pointsShootoutLoss: value } });
                      stamp();
                    }}
                  />
                </Field>
              </div>
              <Field
                label="Standings tiebreakers"
                hint="Used by new season standings and playoff seeding. Existing seasons keep their own settings."
              >
                <div className="stack" style={{ gap: 8 }}>
                  {league.eventDefaults.season.tiebreakers.map((key, index) => (
                    <div key={key} className="row row--between" style={{ gap: 8 }}>
                      <span><span className="kbd">{index + 1}</span> {TIEBREAKER_LABEL[key]}</span>
                      <span className="row" style={{ gap: 4 }}>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={index === 0}
                          onClick={() => moveSeasonDefaultTiebreaker(key, -1)}
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={index === league.eventDefaults.season.tiebreakers.length - 1}
                          onClick={() => moveSeasonDefaultTiebreaker(key, 1)}
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={league.eventDefaults.season.tiebreakers.length <= 1}
                          onClick={() => toggleSeasonDefaultTiebreaker(key, false)}
                        >
                          Remove
                        </button>
                      </span>
                    </div>
                  ))}
                  {TIEBREAKER_OPTIONS.some((key) => !league.eventDefaults.season.tiebreakers.includes(key)) && (
                    <>
                      <div className="divider" />
                      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {TIEBREAKER_OPTIONS.filter((key) => !league.eventDefaults.season.tiebreakers.includes(key)).map((key) => (
                          <button
                            key={key}
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => toggleSeasonDefaultTiebreaker(key, true)}
                          >
                            + {TIEBREAKER_LABEL[key]}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard title="Season Registration & Playoffs" description="Prefills the roster intake, registration, and playoff setup steps.">
            <div className="stack">
              <Field label="Roster intake path">
                <select
                  className="select"
                  value={league.eventDefaults.season.rosterIntake}
                  onChange={(event) => {
                    updateEventDefaults({ season: { rosterIntake: event.target.value as SeasonRosterIntake } });
                    stamp();
                  }}
                >
                  <option value="upload">Import from Directory or CSV</option>
                  <option value="manual">Build manually</option>
                  <option value="registration">Open public registration</option>
                  <option value="later">Decide later</option>
                </select>
              </Field>
              <div className="grid grid--2">
                <Field label="Registration fee">
                  <input
                    type="number"
                    min={0}
                    className="input"
                    value={league.eventDefaults.season.registrationFeeCents / 100}
                    onChange={(event) => {
                      updateEventDefaults({ season: { registrationFeeCents: Math.max(0, Math.round(Number(event.target.value || 0) * 100)) } });
                      stamp();
                    }}
                  />
                </Field>
                <Field label="Registration intake">
                  <select
                    className="select"
                    value={league.eventDefaults.season.registrationIntakeType}
                    onChange={(event) => {
                      updateEventDefaults({ season: { registrationIntakeType: event.target.value as RegistrationIntakeType } });
                      stamp();
                    }}
                  >
                    <option value="individual">Individual players</option>
                    <option value="team">Team registration</option>
                    <option value="both">Teams and individuals</option>
                  </select>
                </Field>
              </div>
              <Switch
                checked={league.eventDefaults.season.registrationRequireWaiver}
                onChange={(value) => {
                  updateEventDefaults({ season: { registrationRequireWaiver: value } });
                  stamp();
                }}
                label="Require waiver by default"
              />
              <Switch
                checked={league.eventDefaults.season.registrationAutoApprove}
                onChange={(value) => {
                  updateEventDefaults({ season: { registrationAutoApprove: value } });
                  stamp();
                }}
                label="Auto-approve registrations by default"
              />
              <Switch
                checked={league.eventDefaults.season.playoffsEnabled}
                onChange={(value) => {
                  updateEventDefaults({ season: { playoffsEnabled: value } });
                  stamp();
                }}
                label="Include playoffs by default"
              />
              {league.eventDefaults.season.playoffsEnabled && (
                <div className="grid grid--2">
                  <Field label="Playoff format">
                    <select
                      className="select"
                      value={league.eventDefaults.season.playoffFormat}
                      onChange={(event) => {
                        updateEventDefaults({ season: { playoffFormat: event.target.value as PlayoffFormat } });
                        stamp();
                      }}
                    >
                      <option value="single_elim">Single elimination</option>
                      <option value="double_elim">Double elimination</option>
                      <option value="top_n_round_robin">Top teams round robin</option>
                    </select>
                  </Field>
                  <Field label="Qualifying teams">
                    <input
                      type="number"
                      min={2}
                      max={64}
                      className="input"
                      value={league.eventDefaults.season.playoffQualifyingTeams}
                      onChange={(event) => {
                        updateEventDefaults({ season: { playoffQualifyingTeams: clampInt(event.target.value, 2, 64) } });
                        stamp();
                      }}
                    />
                  </Field>
                </div>
              )}
            </div>
          </SettingsCard>
        </SettingsGrid>
      )}

      {activeTab === 'communications' && (
        <SettingsGrid>
          <SettingsCard
            title="Communication Hub Defaults"
            description="Defaults applied when a composed message is sent. Recipients, subject, body, preview, and Scorekeeper Intelligence drafts stay in Communication Hub."
            wide
          >
            <div className="stack">
              <Field label="Sender name" hint={`Shows in the email From line. Delivery email: ${deliveryConfig?.fromEmail || 'not configured'}.`}>
                <DebouncedInput value={league.communicationSettings.senderName} onCommit={(value) => {
                  updateCommunicationSettings({ senderName: value });
                  stamp();
                }} />
              </Field>
              <Field label="Reply-to email" hint={`Replies go here. If blank, messages use the league contact email, then ${deliveryConfig?.replyToEmail || 'the configured delivery fallback'}.`}>
                <DebouncedInput type="email" value={league.communicationSettings.replyToEmail} onCommit={(value) => {
                  updateCommunicationSettings({ replyToEmail: value });
                  stamp();
                }} />
              </Field>
              <Field label="Email footer" hint="Appended to outbound email messages. Use {{league}} for the league name.">
                <DebouncedInput textarea rows={3} value={league.communicationSettings.footerText} onCommit={(value) => {
                  updateCommunicationSettings({ footerText: value });
                  stamp();
                }} />
              </Field>
              <Field label="Preference / opt-out copy" hint="Appended below the footer so recipients know how to update communication preferences.">
                <DebouncedInput textarea rows={3} value={league.communicationSettings.optOutText} onCommit={(value) => {
                  updateCommunicationSettings({ optOutText: value });
                  stamp();
                }} />
              </Field>
            </div>
          </SettingsCard>
        </SettingsGrid>
      )}

      {activeTab === 'login' && (
        <SettingsGrid>
          <SettingsCard
            title="Login History"
            description="Recent successful Google sign-ins for this League HQ."
            wide
          >
            {auditRows.filter(isLoginAuditRow).length === 0 ? (
              <div className="empty">No login history recorded yet. New successful Google sign-ins will appear here.</div>
            ) : (
              <table className="table">
                <thead><tr><th>Email</th><th>When</th><th>Source</th></tr></thead>
                <tbody>
                  {auditRows.filter(isLoginAuditRow).slice(0, 25).map((row) => (
                    <tr key={row.id}>
                      <td className="muted">{row.actor_email ?? '-'}</td>
                      <td className="muted">{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</td>
                      <td className="muted">{row.record_table === 'auth' ? 'Google' : row.record_table ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SettingsCard>
        </SettingsGrid>
      )}
    </AdminModulePage>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      {children}
      {hint && <p className="field__hint">{hint}</p>}
    </div>
  );
}

function DebouncedInput({
  autoComplete,
  onCommit,
  placeholder,
  rows,
  style,
  textarea,
  type = 'text',
  value,
}: {
  autoComplete?: string;
  onCommit: (value: string) => void;
  placeholder?: string;
  rows?: number;
  style?: CSSProperties;
  textarea?: boolean;
  type?: HTMLInputTypeAttribute;
  value: string;
}) {
  const [draft, setDraft] = useState(value);
  const onCommitRef = useRef(onCommit);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const timer = window.setTimeout(() => onCommitRef.current(draft), 600);
    return () => window.clearTimeout(timer);
  }, [draft, value]);

  const commitNow = () => {
    if (draft !== value) onCommitRef.current(draft);
  };

  if (textarea) {
    return (
      <textarea
        className="input"
        rows={rows}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitNow}
        placeholder={placeholder}
        style={style}
      />
    );
  }

  return (
    <input
      autoComplete={autoComplete}
      className="input"
      type={type}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commitNow}
      placeholder={placeholder}
      style={style}
    />
  );
}

function SettingsGrid({ children }: { children: ReactNode }) {
  return <div className="settings-grid">{children}</div>;
}

function SettingsCard({
  title,
  description,
  children,
  wide = false,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <section className={`card settings-card ${wide ? 'settings-card--wide' : ''}`}>
      <div className="card__head">
        <div>
          <h3 className="card__title">{title}</h3>
          {description && <p className="card__sub">{description}</p>}
        </div>
      </div>
      <div className="settings-card__body">{children}</div>
    </section>
  );
}

function splitList(value: string): string[] {
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

async function lookupZip(zip: string): Promise<{ city: string; stateCode: string } | null> {
  try {
    const response = await fetch(`/api/geo/zip?zip=${encodeURIComponent(zip)}`, { credentials: 'same-origin' });
    if (!response.ok) return null;
    const data = (await response.json()) as { city?: string; stateCode?: string };
    return data.city && data.stateCode ? { city: data.city, stateCode: data.stateCode } : null;
  } catch {
    return null;
  }
}

function isLoginAuditRow(row: AuditRow): boolean {
  return row.action === 'auth.login';
}

function clampInt(value: string, min: number, max: number): number {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result ?? '');
      resolve(value.includes(',') ? value.split(',').pop() ?? '' : value);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });
}
